import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaShieldAlt, FaClock, FaTrophy, FaStar, FaGift, FaBullseye, FaCreditCard, FaPaypal, FaQuestionCircle, FaExclamationTriangle, FaUsers, FaAward, FaFutbol } from 'react-icons/fa';
import { MdSportsBasketball, MdSportsTennis, MdSportsHockey } from 'react-icons/md';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const Home: React.FC = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Parismin - Guide des Paris Sportifs en France',
    url: 'https://parismin.com',
    description: 'Découvrez les meilleures offres de paris sportifs en France. Comparaisons détaillées, avis experts et bonus exclusifs.',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://parismin.com/reviews?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const topBookmakers = [
    {
      name: 'Winamax',
      logo: '/images/offers/winamax.webp',
      url: 'https://www.winamax.fr/',
      rating: 4.8,
      bonus: '150€',
      features: ['Poker intégré', 'Promos quotidiennes', 'App mobile'],
      color: 'from-blue-500 to-cyan-600',
    },
    {
      name: 'Bwin',
      logo: '/images/offers/bwin.webp',
      url: 'https://bwin.fr/',
      rating: 4.7,
      bonus: '100€',
      features: ['Cotes compétitives', 'Live streaming', 'Cash-out'],
      color: 'from-green-500 to-emerald-600',
    },
    {
      name: 'Unibet',
      logo: '/images/offers/unibet.webp',
      url: 'https://www.unibet.fr/',
      rating: 4.6,
      bonus: '120€',
      features: ['Bonus récurrents', 'Live betting', 'VIP program'],
      color: 'from-purple-500 to-pink-600',
    },
  ];

  const features = [
    {
      icon: FaShieldAlt,
      title: 'Sécurité Garantie',
      description: 'Tous les bookmakers sont licenciés par l\'ANJ et vérifiés pour leur fiabilité.',
    },
    {
      icon: FaClock,
      title: 'Paiements Rapides',
      description: 'Retraits traités en moins de 24h sur les meilleurs sites de paris.',
    },
    {
      icon: FaTrophy,
      title: 'Meilleures Cotes',
      description: 'Comparaison en temps réel pour maximiser vos gains potentiels.',
    },
    {
      icon: FaGift,
      title: 'Bonus Exclusifs',
      description: 'Accès à des offres promotionnelles réservées à nos lecteurs.',
    },
  ];

  return (
    <Layout>
      <SEO
        title="Guide Complet des Paris Sportifs en France"
        description="Comparez les meilleurs bookmakers français. Avis détaillés, bonus exclusifs et conseils d'experts pour optimiser vos paris sportifs."
        url="/"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative min-h-[95vh] flex items-center justify-center overflow-hidden">
        {/* Animated Background Particles */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="particle w-64 h-64 top-10 left-10" style={{ animationDelay: '0s' }}></div>
          <div className="particle w-96 h-96 top-1/2 right-10" style={{ animationDelay: '5s' }}></div>
          <div className="particle w-80 h-80 bottom-20 left-1/3" style={{ animationDelay: '10s' }}></div>
        </div>
        
        {/* Gradient Orbs - Optimized to prevent CLS */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ contain: 'strict', willChange: 'opacity', transform: 'translateZ(0)', pointerEvents: 'none' }}></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s', contain: 'strict', willChange: 'opacity', transform: 'translateZ(0)', pointerEvents: 'none' }}></div>
        {/* Removed problematic center orb (0.127 CLS) - using CSS background gradient instead */}
        
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="mb-8 inline-flex items-center space-x-2 px-6 py-3 glass-card rounded-full text-sm font-semibold border border-cyan-500/30 max-md:hidden">
            <FaBullseye className="text-cyan-400 animate-pulse" />
            <span className="text-gradient-gold">Guide Expert 2025</span>
          </div>
          <h1 className="text-6xl md:text-8xl font-black mb-8 leading-tight heading-font">
            <span className="text-gradient block mb-2">Les Meilleurs</span>
            <span className="text-white block">Bookmakers</span>
            <span className="text-gradient block mt-2">de France</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Comparez, choisissez et profitez des meilleures offres de paris sportifs. 
            Notre équipe teste chaque bookmaker pour vous garantir la meilleure expérience.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center mb-12">
            <Link
              to="/reviews"
              className="group relative px-10 py-5 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-2xl font-bold text-lg text-white overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl neon-glow flex items-center justify-center space-x-2"
            >
              <span className="relative z-10">Voir les Avis</span>
              <FaArrowRight className="relative z-10 group-hover:translate-x-1 transition-transform" />
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </Link>
            <Link
              to="/comparisons"
              className="px-10 py-5 glass-card rounded-2xl font-bold text-lg hover:bg-white/10 transition-all duration-300 hover:scale-105 border border-white/20 flex items-center justify-center space-x-2"
            >
              <span>Comparer les Offres</span>
              <FaArrowRight />
            </Link>
          </div>

          {/* Anchor Links */}
          <div className="relative z-10 container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
              {[
                { label: 'Top Bookmakers', href: '#top-bookmakers', icon: FaTrophy },
                { label: 'À Propos', href: '#about', icon: FaUsers },
                { label: 'Sports', href: '#sports', icon: FaFutbol },
                { label: 'Paiements', href: '#payments', icon: FaCreditCard },
                { label: 'FAQ', href: '#faq', icon: FaQuestionCircle },
                { label: 'Jeu Responsable', href: '#responsible', icon: FaShieldAlt },
              ].map((link, index) => {
                const Icon = link.icon;
                return (
                  <a
                    key={index}
                    href={link.href}
                    className="group flex items-center space-x-2 px-5 py-3 glass-card rounded-xl font-semibold text-sm hover:bg-white/10 transition-all duration-300 hover:scale-105"
                  >
                    <Icon className="text-cyan-400 group-hover:text-cyan-300 transition-colors" />
                    <span>{link.label}</span>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Top Bookmakers Section */}
      <section id="top-bookmakers" className="py-20 relative scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-1.5 glass rounded-full text-xs font-semibold text-cyan-400 uppercase tracking-wider">
              Sélection Premium
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-6 heading-font">
              <span className="text-gradient">Top 3 Bookmakers</span>
            </h2>
            <p className="text-gray-400 text-xl max-w-2xl mx-auto">
              Les sites les mieux notés par notre équipe d'experts
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {topBookmakers.map((bookmaker, index) => (
              <div
                key={bookmaker.name}
                className="glass-card rounded-3xl p-8 card-hover relative overflow-hidden group"
              >
                {/* Gradient Background Glow */}
                <div className={`absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br ${bookmaker.color} rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-opacity duration-500`} style={{ contain: 'layout style paint' }}></div>
                <div className={`absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br ${bookmaker.color} rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-500`} style={{ contain: 'layout style paint' }}></div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3 flex-col">
                      <img 
                        src={bookmaker.logo} 
                        alt={bookmaker.name}
                        className="h-12 w-auto object-contain"
                        width="166"
                        height="48"
                        loading="eager"
                        decoding="async"
                      />
                      <h3 className="text-3xl font-black heading-font">{bookmaker.name}</h3>
                    </div>
                    <div className="flex items-center space-x-1.5 px-3 py-1.5 glass rounded-full">
                      <FaStar className="text-yellow-400 text-sm" />
                      <span className="font-bold text-sm">{bookmaker.rating}</span>
                    </div>
                  </div>
                  <div className="mb-8 p-5 glass rounded-2xl border border-white/10">
                    <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">Bonus de bienvenue</div>
                    <div className="text-4xl font-black text-gradient-gold">{bookmaker.bonus}</div>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {bookmaker.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-3 text-gray-300">
                        <div className="w-2 h-2 bg-gradient-to-r from-cyan-400 to-purple-400 rounded-full"></div>
                        <span className="font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="space-y-3">
                    <a
                      href={bookmaker.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full py-4 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-xl text-center font-bold text-white hover:shadow-xl hover:shadow-purple-500/50 transition-all duration-300 group/btn"
                    >
                      <span className="flex items-center justify-center space-x-2">
                        <span>Visiter le site</span>
                        <FaArrowRight className="group-hover/btn:translate-x-1 transition-transform" />
                      </span>
                    </a>
                    <Link
                      to={`/reviews#${bookmaker.name.toLowerCase()}`}
                      className="block w-full py-3 glass rounded-xl text-center font-semibold text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300"
                    >
                      Voir l'avis complet
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-1.5 glass rounded-full text-xs font-semibold text-purple-400 uppercase tracking-wider">
              Avantages
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-6 heading-font">
              Pourquoi <span className="text-gradient">Choisir Parismin</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="glass-card rounded-3xl p-8 card-hover text-center relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="relative z-10">
                    <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                      <Icon className="text-3xl text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-3 heading-font">{feature.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 relative scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block mb-4 px-4 py-1.5 glass rounded-full text-xs font-semibold text-cyan-400 uppercase tracking-wider">
                À Propos
              </div>
              <h2 className="text-5xl md:text-6xl font-black mb-6 heading-font">
                <span className="text-gradient">Qui Sommes-Nous</span>?
              </h2>
            </div>
            <div className="glass-card rounded-3xl p-10 md:p-16 text-center">
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-6">
                Parismin est votre guide de confiance pour les paris sportifs en France. Notre équipe d'experts teste et compare 
                les meilleurs bookmakers pour vous offrir des analyses détaillées et objectives.
              </p>
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
                Nous nous engageons à vous fournir des informations transparentes, des comparaisons précises et des recommandations 
                basées sur l'expérience réelle de chaque plateforme. Notre mission est de vous aider à faire les meilleurs choix 
                pour optimiser vos paris sportifs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Sports Section */}
      <section id="sports" className="py-24 relative scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-1.5 glass rounded-full text-xs font-semibold text-purple-400 uppercase tracking-wider">
              Sports
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-6 heading-font">
              <span className="text-gradient">Sports Populaires</span>
            </h2>
            <p className="text-gray-400 text-xl max-w-2xl mx-auto">
              Les sports les plus populaires pour les paris en France
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              { name: 'Football', icon: FaFutbol, color: 'from-green-500 to-emerald-600' },
              { name: 'Basketball', icon: MdSportsBasketball, color: 'from-orange-500 to-red-600' },
              { name: 'Tennis', icon: MdSportsTennis, color: 'from-yellow-500 to-orange-600' },
              { name: 'Hockey', icon: MdSportsHockey, color: 'from-blue-500 to-cyan-600' },
            ].map((sport, index) => {
              const Icon = sport.icon;
              return (
                <div
                  key={index}
                  className="glass-card rounded-2xl p-8 text-center card-hover group"
                >
                  <div className={`w-20 h-20 bg-gradient-to-br ${sport.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                    <Icon className="text-4xl text-white" />
                  </div>
                  <h3 className="text-xl font-bold heading-font">{sport.name}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section id="payments" className="py-24 relative scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-1.5 glass rounded-full text-xs font-semibold text-pink-400 uppercase tracking-wider">
              Paiements
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-6 heading-font">
              <span className="text-gradient">Méthodes de Paiement</span>
            </h2>
            <p className="text-gray-400 text-xl max-w-2xl mx-auto">
              Options de dépôt et retrait sécurisées disponibles
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { name: 'Carte Bancaire', icon: FaCreditCard, description: 'Visa, Mastercard acceptées' },
              { name: 'PayPal', icon: FaPaypal, description: 'Paiement instantané et sécurisé' },
              { name: 'Virement Bancaire', icon: FaCreditCard, description: 'Transfert direct depuis votre banque' },
            ].map((method, index) => {
              const Icon = method.icon;
              return (
                <div
                  key={index}
                  className="glass-card rounded-2xl p-8 text-center card-hover"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icon className="text-2xl text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2 heading-font">{method.name}</h3>
                  <p className="text-gray-400 text-sm">{method.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 relative scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-1.5 glass rounded-full text-xs font-semibold text-yellow-400 uppercase tracking-wider">
              Questions
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-6 heading-font">
              <span className="text-gradient">Questions Fréquentes</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: 'Comment choisir le meilleur bookmaker ?',
                answer: 'Nous recommandons de comparer les cotes, les bonus, les méthodes de paiement et les avis des utilisateurs. Notre guide vous aide à faire le meilleur choix selon vos besoins.',
              },
              {
                question: 'Les bookmakers sont-ils sûrs et légaux ?',
                answer: 'Oui, tous les bookmakers que nous recommandons sont licenciés par l\'Autorité Nationale des Jeux (ANJ) et respectent les réglementations françaises en matière de paris sportifs.',
              },
              {
                question: 'Combien de temps pour recevoir mes gains ?',
                answer: 'Les délais varient selon le bookmaker et la méthode de retrait. En général, les retraits sont traités entre 12h et 48h. Les cartes bancaires sont généralement plus rapides que les virements.',
              },
              {
                question: 'Puis-je annuler un pari ?',
                answer: 'Cela dépend du bookmaker et du type de pari. Certains offrent la fonctionnalité "Cash-out" qui permet de clôturer votre pari avant la fin de l\'événement.',
              },
              {
                question: 'Y a-t-il des frais sur les retraits ?',
                answer: 'La plupart des bookmakers français n\'appliquent pas de frais sur les retraits, mais cela peut varier selon la méthode choisie. Vérifiez toujours les conditions avant de retirer.',
              },
            ].map((faq, index) => (
              <div key={index} className="glass-card rounded-2xl p-6 md:p-8 card-hover">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FaQuestionCircle className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-3 heading-font">{faq.question}</h3>
                    <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Responsible Gaming Section */}
      <section id="responsible" className="py-24 relative scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="glass-card rounded-3xl p-10 md:p-16 max-w-5xl mx-auto border-2 border-yellow-500/30">
            <div className="text-center mb-10">
              <div className="w-20 h-20 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <FaExclamationTriangle className="text-white text-3xl" />
              </div>
              <h2 className="text-4xl md:text-5xl font-black mb-4 heading-font">
                <span className="text-gradient-gold">Jeu Responsable</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Le jeu doit rester un divertissement. Jouez de manière responsable.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {[
                { icon: FaUsers, title: '18+ Uniquement', text: 'Les paris sportifs sont réservés aux personnes majeures de 18 ans et plus.' },
                { icon: FaAward, title: 'Limites de Dépôt', text: 'Fixez vos propres limites de dépôt et de perte pour contrôler votre budget.' },
                { icon: FaClock, title: 'Pause de Jeu', text: 'Utilisez les outils de pause ou d\'auto-exclusion si vous en ressentez le besoin.' },
                { icon: FaShieldAlt, title: 'Aide Disponible', text: 'En cas de problème, contactez Joueurs Info Service au 09-74-75-13-13.' },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="glass rounded-xl p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 heading-font">{item.title}</h3>
                        <p className="text-gray-300 text-sm">{item.text}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-center pt-6 border-t border-white/10">
              <p className="text-gray-400 text-sm mb-4">
                Besoin d'aide ? Contactez le service d'aide aux joueurs :
              </p>
              <a 
                href="https://www.joueurs-info-service.fr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 px-6 py-3 glass rounded-xl font-semibold hover:bg-white/10 transition-all"
              >
                <span>Joueurs Info Service</span>
                <FaArrowRight />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <div className="glass-card rounded-3xl p-12 md:p-20 text-center relative overflow-hidden border-2 border-cyan-500/30">
            {/* Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10"></div>
            <div className="absolute top-0 left-0 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-72 h-72 bg-pink-500/20 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <h2 className="text-5xl md:text-6xl font-black mb-6 heading-font">
                Prêt à <span className="text-gradient">Commencer</span>?
              </h2>
              <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
                Découvrez notre sélection de bookmakers vérifiés et profitez des meilleures offres du marché.
              </p>
              <Link
                to="/reviews"
                className="group inline-flex items-center space-x-3 px-12 py-6 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-2xl font-black text-lg text-white hover:shadow-2xl neon-glow transition-all duration-300 hover:scale-105"
              >
                <span>Explorer les Bookmakers</span>
                <FaArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;

